import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemeMode = 'dark' | 'light';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'omniforge:themeMode';
  private readonly modeSubject = new BehaviorSubject<ThemeMode>('dark');
  private assetsBooted = false;

  readonly mode$ = this.modeSubject.asObservable();

  init() {
    const mode = this.getMode();
    this.apply(mode);
    this.modeSubject.next(mode);
    void this.applyBackgroundAssets();
  }

  toggle() {
    const next: ThemeMode = this.getMode() === 'dark' ? 'light' : 'dark';
    localStorage.setItem(this.storageKey, next);
    this.apply(next);
    this.modeSubject.next(next);
  }

  get currentMode(): ThemeMode {
    return this.modeSubject.value;
  }

  private getMode(): ThemeMode {
    const raw = localStorage.getItem(this.storageKey);
    return raw === 'light' ? 'light' : 'dark';
  }

  private apply(mode: ThemeMode) {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset['theme'] = mode;
  }

  private async applyBackgroundAssets() {
    if (this.assetsBooted || typeof document === 'undefined' || typeof fetch === 'undefined') {
      return;
    }
    this.assetsBooted = true;

    const assetExists = async (url: string) => {
      try {
        const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        return response.ok;
      } catch {
        return false;
      }
    };

    try {
      const [heroWebp, textureWebp, worldBannerWebp] = await Promise.all([
        assetExists('/assets/backgrounds/of-hero-arcane-smithy.webp'),
        assetExists('/assets/backgrounds/of-texture-hammered-iron.webp'),
        assetExists('/assets/backgrounds/of-banner-world.webp'),
      ]);

      document.documentElement.dataset['bgAssets'] = heroWebp && textureWebp ? 'webp' : 'svg';
      document.documentElement.dataset['bgWorldBanner'] = worldBannerWebp ? 'webp' : 'none';
    } catch {
      document.documentElement.dataset['bgAssets'] = 'svg';
      document.documentElement.dataset['bgWorldBanner'] = 'none';
    }
  }
}
